import PixelConverter, { WH } from '../../utilities/pixel-converter';
import { Pos } from '../../models/pos';
import { UuidGenerator } from '../../utilities/uuid-generator';
import { TrimTextAlignCodeType } from '../manager/image-edit/text/trim.text-controller';
import { TrimDataManager } from '../manager/data/trim-data.manager';
import { ColorCodeConverter } from '../../utilities/colorcode-converter';

export const DATE_TEXT_ID = '_dateText';

export class RealObjectBounding {
  constructor(
    public wh: WH,
    public pos: Pos,
  ) {
  }
}

export class VirtualObjectBounding {
  constructor(
    public wh: WH,
    public pos: Pos,
  ) {
    this.wh = {
      width: Math.round(PixelConverter.mmToPx(this.wh.width) / TrimDataManager.getAdditionalValue),
      height: Math.round(PixelConverter.mmToPx(this.wh.height) / TrimDataManager.getAdditionalValue),
    };
    this.pos = {
      x: Math.round(PixelConverter.mmToPx(this.pos.x) / TrimDataManager.getAdditionalValue),
      y: Math.round(PixelConverter.mmToPx(this.pos.y) / TrimDataManager.getAdditionalValue),
    };
  }
}

export type TemplateObjType = 'template' | 'dateText' | 'tempText';
export type WindowObjType = 'image' | 'dateText' | 'tempText' | 'text' | 'stamp';

export class PrintBase {
  constructor(
    public id: string,
    public real: WH,
    public window: WindowBase[],
  ) {
  }

  addWindow(window: WindowBase) {
    this.window.push(window);
  }

  getImageWindow() {
    return this.window.find(v => v.object?.objType === 'image');
  }

  getFillWindow() {
    return this.window.filter(v => v.fill);
  }

  getDateTextWindow() {
    return this.window.find(v => v.object?.objType === 'dateText');
  }
}

export class WindowBase {
  constructor(
    public id: string,
    public depth: number,
    public real: RealObjectBounding,
    public virtual: VirtualObjectBounding,
    public object?: ObjectBase,
    public fill?: FillBase,
  ) {
  }
}

export class ObjectBase {
  constructor(
    public id: string,
    public bound: VirtualObjectBounding,
    public rotation: number,
    public objType: WindowObjType,
    public type?: string,
    public text?: TextBase,
  ) {
  }
}

export class FillBase {
  constructor(
    public enable: number,
    public color: string,
  ) {
  }
}

export class TextStyle {
  constructor(
    public outline: { width: number, color: string },
    public color: string,
    public bold: boolean,
    public italic: boolean,
    public alignment: TrimTextAlignCodeType,
  ) {
  }
}

export class TextBase {
  constructor(
    public id: string,
    public value: string,
    public point: number,
    public font: string,
    public style: TextStyle,
  ) {
  }
}

export class TempObjectBounding {
  constructor(
    public wh: WH,
    public pos: Pos,
  ) {
  }
}

export class TempPrintBase {
  constructor(
    public id: string,
    public wh: WH,
    public window: TempWindowBase[],
  ) {
  }

  addWindow(window: TempWindowBase) {
    this.window.push(window);
  }

  getTempWindow() {
    return this.window.find(v => v.object?.objType === 'template');
  }

  getFillWindow() {
    return this.window.filter(v => v.fill);
  }

  getTextWindow() {
    // console.warn(this.window);
    return this.window.filter(v => v.object?.objType === 'tempText');
  }
  getDateTextWindow() {
    // console.warn(this.window);
    return this.window.find(v => v.object?.objType === 'dateText');
  }
}

export class TempWindowBase {
  constructor(
    public id: string,
    public depth: number,
    public bound: TempObjectBounding,
    public attribute: string,
    public object?: TempObjectBase,
    public fill?: TempFillBase,
    public rotation?: number,
  ) {
  }
}

export class TempObjectBase {
  constructor(
    public objType: TemplateObjType,
    public id?: string,
    public type?: string,
    public text?: TempTextBase,
  ) {
  }
}

export class TempFillBase {
  constructor(
    public enable: number,
    public color: string,
  ) {
  }
}

export class TempTextStyle {
  constructor(
    public outline: { width: number, color: string } = { width: 0.3, color: '#ffffff' },
    public color: string = '#000000',
    public bold: boolean = false,
    public italic: boolean = false,
    public alignment: TrimTextAlignCodeType = '0',
  ) {
  }
}

export class TempTextBase {
  constructor(
    public value: string,
    public point: number,
    public font: string,
    public antialiasEnable: number,
    public style: TempTextStyle,
  ) {
  }
}

export type TempTextDataEntity = {
  depth: number,
  x: number,
  y: number,
  width: number,
  height: number,
  attribute: string,
  edge: { width: number, color: string },
  antialiasEnable: number,
  style: { face: string, point: number },
  value: string,
}
export type TempFillDataEntity = {
  depth: number,
  x: number,
  y: number,
  width: number,
  height: number,
  attribute: string,
  enable: number,
  color: string,
}

export class TrimTempDataEntity {
  constructor(
    public base: { width: number, height: number },
    public dateText?: TempTextDataEntity,
    public tempTexts: TempTextDataEntity[] = [],
    public fills: TempFillDataEntity[] = [],
  ) {
  }

  setTempTexts(texts: TempTextDataEntity[]) {
    this.tempTexts = [...texts];
  }
  setFills(fills: TempFillDataEntity[]) {
    this.fills = [...fills];
  }
}

export type PrintTrimDataBase = {
  id: string,
  itemId: string,
  config: TrimConfigEntity,
  temp: TrimTempDataEntity,
  image: TrimImageDataEntity,
  texts: TrimTextDataEntity[],
  stamps: TrimImageDataEntity[],
  view: TrimPrintViewDataEntity;
}

export type TrimImageDataEntity = {
  id: string,
  depth: number,
  fileName: string,
  x: number,
  y: number,
  width: number,
  height: number,
  rotate: number,
  scale: number,
  frame?: {
    x: number,
    y: number,
    width: number,
    height: number,
  }
}

export class TrimImageDataEntityInit implements TrimImageDataEntity{
  id: string;
  depth: number;
  fileName: string = '';
  x: number = 0;
  y: number = 0;
  width: number = 0;
  height: number = 0;
  rotate: number = 0;
  scale: number = 0;
  frame?: {
    x: number,
    y: number,
    width: number,
    height: number,
  }
  constructor(id: string, depth: number, frame?: RealObjectBounding) {
    this.id = id;
    this.depth = depth;
    this.frame = frame ? {
      x: frame.pos.x,
      y: frame.pos.y,
      width: frame.wh.width,
      height: frame.wh.height,
    } : undefined;
  }
}

export type TrimTextDataEntity = {
  id: string,
  depth: number,
  x: number,
  y: number,
  width: number,
  height: number,
  value: string,
  point: number,
  font: string,
  style: {
    bold: boolean,
    italic: boolean,
    outline: { width: number, color: string },
    color: string,
  },
  alignment: '0' | '1' | '2',
  rotate: number,
  scale: number,
}

export type TrimConfigEntity = {
  space: 0 | 1,
  trimBaseType: 1 | 2 | 3,
}

export type TrimViewDataEntity = {
  id: string,
  fileName: string,
  pos: Pos,
  rotate: number,
  scale: number,
}

export class TrimViewDataEntityInit implements TrimViewDataEntity {
  id: string = '';
  fileName: string = '';
  pos: Pos = { x: 0, y: 0 };
  rotate: number = 0;
  scale: number = 0;
}

export type TrimPrintViewDataEntity = {
  image: TrimViewDataEntity,
  texts: TrimViewDataEntity[],
  stamps: TrimViewDataEntity[],
}

export class TrimPrintViewDataEntityInit implements TrimPrintViewDataEntity {
  image: TrimViewDataEntity = new TrimViewDataEntityInit();
  texts: TrimViewDataEntity[] = [];
  stamps: TrimViewDataEntity[] = [];
}

export class PrintTrimData implements PrintTrimDataBase {
  fileName: string;
  id: string;
  itemId: string;
  config: TrimConfigEntity;
  temp: TrimTempDataEntity;
  image: TrimImageDataEntity;
  texts: TrimTextDataEntity[];
  stamps: TrimImageDataEntity[];
  view: TrimPrintViewDataEntity;

  constructor(fileName: string, data: PrintTrimDataBase) {
    this.fileName = fileName;
    this.id = data.id;
    this.itemId = data.itemId;
    this.config = data.config;
    this.temp = data.temp;
    this.image = data.image;
    this.texts = data.texts;
    this.stamps = data.stamps;
    this.view = data.view;
  }

  exportJson() {
    return {
      filename: this.fileName || `${this.id}.json`,
      body: JSON.stringify(this),
    }
  }

  addText(text: TrimTextDataEntity) {
    this.texts.push(text);
  }

  addStamp(stamp: TrimImageDataEntity) {
    this.stamps.push(stamp);
  }
  addTextView(text: TrimViewDataEntity) {
    this.view.texts.push(text);
  }

  addStampView(stamp: TrimViewDataEntity) {
    this.view.stamps.push(stamp);
  }

  setImageTrimData(baseSize: WH, z: number, movePos: Pos, rotate: number, scale: number) {

    const maskW = (baseSize.width / 2 / scale) - ((movePos.x) / scale);
    const maskH = (baseSize.height / 2 / scale) - ((movePos.y) / scale);

    this.image.x = PixelConverter.pxToMm((maskW) * z) - (this.image.frame?.x ?? 0);
    this.image.y = PixelConverter.pxToMm((maskH) * z) - (this.image.frame?.y ?? 0);
    this.image.width = PixelConverter.pxToMm(baseSize.width / scale * z);
    this.image.height = PixelConverter.pxToMm(baseSize.height / scale * z);
    this.image.rotate = rotate;
  }

  setImageTrimViewData(data: TrimViewDataEntity) {
    this.view.image = data;
  }

  getImageMovePos(imageBaseSize: WH, defaultWH: WH, scale: number) {
    if (!this.image.x && !this.image.y) {
      return {x: 0, y: 0};
    }
    const base = this.image.frame ?? this.temp.base;
    const tempSize: WH = {width: PixelConverter.mmToPx(base.width), height: PixelConverter.mmToPx(base.height)};
    const z = tempSize.width / imageBaseSize.width;

    return {
      x:  (PixelConverter.mmToPx(this.image.x) / z) - (defaultWH.width / TrimDataManager.getAdditionalValue / scale / 2),
      y:  (PixelConverter.mmToPx(this.image.y) / z) - (defaultWH.height / TrimDataManager.getAdditionalValue / scale / 2),
    }
  }
  getImageScale(imageBaseSize: WH) {
    if (!this.image.height || !this.image.width) return 0;
    const base = this.image.frame ?? this.temp.base;
    const tempSize: WH = {width: PixelConverter.mmToPx(base.width), height: PixelConverter.mmToPx(base.height)};
    const z = tempSize.width / imageBaseSize.width;
    return  (tempSize.width / TrimDataManager.getAdditionalValue * z) / PixelConverter.mmToPx(this.image.width)
  }

  getImageRotate() {
    return this.image.rotate / 100;
  }

  changeTemp(temp: TempPrintBase) {
    const dateText = temp.getDateTextWindow();
    const tempTexts = temp.getTextWindow();
    const tmpFrame = temp.getTempWindow();
    const fills = temp.getFillWindow();

    const tmpData = new TrimTempDataEntity(
      { width: temp.wh.width, height: temp.wh.height },
      dateText ? {
        depth: dateText.depth,
        x: dateText.bound.pos.x,
        y: dateText.bound.pos.y,
        width: dateText.bound.wh.width,
        height: dateText.bound.wh.height,
        attribute: dateText.attribute,
        edge: {
          color: dateText.object?.text?.style.color ?? '#ffffff',
          width: dateText.object?.text?.style.outline.width ?? 0.3,
        },
        antialiasEnable: dateText.object?.text?.antialiasEnable ?? 1,
        style: { face: dateText.object?.text?.font ?? 'ＭＳ ゴシック', point: dateText.object?.text?.point ?? 90 },
        value: dateText.object?.text?.value ?? '',
      } : undefined,
    );
    tmpData.setTempTexts(tempTexts.map((data): TempTextDataEntity => {
      return {
        depth: data.depth,
        x: data.bound.pos.x,
        y: data.bound.pos.y,
        width: data.bound.wh.width,
        height: data.bound.wh.height,
        attribute: data.attribute,
        edge: {
          color: ColorCodeConverter.Color2Hex(data.object?.text?.style.outline.color ?? '#ffffff'),
          width: data.object?.text?.style.outline.width ?? 0.3,
        },
        antialiasEnable: data.object?.text?.antialiasEnable ?? 1,
        style: { face: data.object?.text?.font ?? 'ＭＳ ゴシック', point: data.object?.text?.point ?? 90 },
        value: data.object?.text?.value ?? '',
      }
    }));
    tmpData.setFills(fills.map((data): TempFillDataEntity => {
      return {
        depth: data.depth,
        x: data.bound.pos.x,
        y: data.bound.pos.y,
        width: data.bound.wh.width,
        height: data.bound.wh.height,
        attribute: data.attribute,
        enable: data.fill?.enable ?? 0,
        color: ColorCodeConverter.Color2Hex(data.fill?.color ?? '#ffffff'),
      }
    }));

    this.temp = tmpData;
    if (tmpFrame) {
      this.image.frame = {
        x: tmpFrame.bound.pos.x,
        y: tmpFrame.bound.pos.y,
        width: tmpFrame.bound.wh.width,
        height: tmpFrame.bound.wh.height,
      }
    }
  }

}
